import type {
  InjectionProtocol,
  ProtocolBrand,
  ProtocolStep,
  ProtocolWarning,
} from "../types";

/**
 * Menotropins — the first medication here whose *numbers* differ by market.
 *
 * Every market split before this one was about devices: Gonal-F's vial is sold
 * in the EEA and not the US, so the walk for it is offered in one place and not
 * the other. Menopur is the same powder, the same solvent and the same syringe
 * everywhere, and the two labels still disagree about what she may do with it.
 *
 * | | US (FDA) | UK/EEA (SmPC) |
 * | --- | --- | --- |
 * | Route | *"for subcutaneous use"* | *"For intramuscular or subcutaneous use"* |
 * | Vials per 1 mL | 1 + *"up to 5 more"* = **six** | *"Up to **three** powder vials"* |
 *
 * So there are two walks, each restricted to its own markets, and neither
 * averages the other. A European patient dissolving six vials would be outside
 * her own label; an American offered the intramuscular route would be outside
 * hers, and in the worse direction.
 *
 * **Both are restricted**, which means a patient whose country we cannot read
 * gets no Menopur walk. That is deliberate and it is `protocolsForMarket`'s
 * documented asymmetry: not offering a walk is a real answer, offering the
 * other market's numbers is not. Leaving one of them unrestricted as a fallback
 * was the first plan and it was wrong — both would then match in Europe, two
 * protocols would answer to one name, and the narrowing downstream would pick
 * one silently.
 *
 * ### Why neither walk covers the intramuscular route
 *
 * Not caution. The UK SmPC permits it — that document is written for the
 * prescriber — but **the patient leaflet teaches only the subcutaneous
 * injection** and gives no intramuscular instructions at all: *"You will either
 * be given Menopur by a doctor or nurse or you will be taught how to give it to
 * yourself."* There is no approved patient-facing procedure to restate, so a
 * walk for it would be one we wrote. Omitting is allowed; inventing is not.
 *
 * ### The market is never named to her
 *
 * The step says "How many vials" and then her own market's rule. The content
 * carries a trailing `(US)` / `(Europe)` marker so the factsheet — which is not
 * filtered by country — does not show two contradictory rows with nothing to
 * tell them apart, and that marker is outside the span each walk restates. She
 * needs the rule, not which regulator wrote it (Anaïs, 2026-09-28).
 *
 * ### The sites, which also differ
 *
 * The US label names the abdomen and nothing else — *"Pick a site on your lower
 * abdomen, 1-2 inches below the navel"*. The European leaflet names the thigh
 * beside it — *"(e.g. front of the thigh, stomach (abdomen), etc)"* — so only
 * the European walk carries `sites`.
 *
 * ### One walk for one vial and for six
 *
 * Both read the multi-vial procedure, because it is the single-vial procedure
 * plus a repetition: "repeat for any additional powder vials". Splitting them
 * would be two walks that differ by whether a loop runs once.
 */

const BRANDS = [
  { name: "Menopur", markets: ["US", "GB", "EU", "CA", "AU", "NZ"] },
  { name: "Menogon", markets: ["DE", "GB", "EU"] },
] as const satisfies readonly ProtocolBrand[];

/** The EEA, the UK and Switzerland — written out, because `protocolsForMarket`
 * compares against a country and an `"EU"` entry would match nobody. */
const EEA_MARKETS = [
  "AT",
  "BE",
  "BG",
  "CH",
  "CY",
  "CZ",
  "DE",
  "DK",
  "EE",
  "ES",
  "FI",
  "FR",
  "GB",
  "GR",
  "HR",
  "HU",
  "IE",
  "IS",
  "IT",
  "LI",
  "LT",
  "LU",
  "LV",
  "MT",
  "NL",
  "NO",
  "PL",
  "PT",
  "RO",
  "SE",
  "SI",
  "SK",
] as const;

/**
 * The same sentence every gonadotropin here carries.
 *
 * The content said "if less than 2 hours late, inject right away". The US label
 * reads, in full: *"if she misses or forgets to take a dose of MENOPUR, the
 * next dose should not be doubled and she should call her healthcare
 * provider."* No hour, in either market. See #3653.
 */
const MISSED_DOSE: ProtocolWarning = {
  id: "menopur.missed-dose",
  severity: "caution",
  escalation: "none",
  copy: {
    en: "Missed or late dose - Never take two doses to catch up. Tell your clinic as soon as you can and follow what they say. If you cannot reach them the same day, take the dose when you remember rather than skipping it, and tell them at your next appointment.",
    fr: "Dose oubliée ou en retard - Ne doublez jamais la dose pour rattraper. Prévenez votre centre dès que possible et suivez ses consignes. Si vous ne pouvez pas les joindre le jour même, faites l'injection quand vous y pensez plutôt que de sauter la dose, et signalez-le à votre prochain rendez-vous.",
  },
  sourceSegment:
    "⚠ Missed or late dose - Never take two doses to catch up. Tell your clinic as soon as you can and follow what they say. If you cannot reach them the same day, take the dose when you remember rather than skipping it, and tell them at your next appointment.",
};

const WHEN_TO_CALL: ProtocolWarning = {
  id: "menopur.when-to-call",
  severity: "stop",
  escalation: "clinic-now",
  copy: {
    en: "When to call your clinic - Stop injecting and call your clinic for rapid weight gain over 2 lbs/day, severe abdominal pain, nausea or vomiting, difficulty breathing, decreased urination, or injection site infection signs.",
    fr: "Quand contacter votre centre - Arrêtez d'injecter et appelez votre centre en cas de prise de poids rapide de plus de 1 kg/jour, douleurs abdominales sévères, nausées ou vomissements, difficultés respiratoires, diminution de la diurèse ou signes d'infection au point d'injection.",
  },
  sourceSegment:
    "⚠ When to call your clinic - Stop injecting and call your clinic for rapid weight gain over 2 lbs/day, severe abdominal pain, nausea or vomiting, difficulty breathing, decreased urination, or injection site infection signs.",
};

/** Anaphylaxis is not a phone call to a fertility clinic (Anaïs, 2026-09-22). */
const BREATHING_OR_ALLERGIC_REACTION: ProtocolWarning = {
  id: "menopur.breathing-or-allergic-reaction",
  severity: "stop",
  escalation: "emergency",
  copy: {
    en: "Seek urgent help - Sudden difficulty breathing, swelling of the face or throat, a rash or hives need emergency services straight away.",
    fr: "Aide urgente - Des difficultés respiratoires soudaines, un gonflement du visage ou de la gorge, une éruption cutanée ou de l'urticaire nécessitent d'appeler les secours tout de suite.",
  },
  sourceSegment:
    "⚠ Seek urgent help - Sudden difficulty breathing, swelling of the face or throat, a rash or hives need emergency services straight away.",
};

/**
 * The thing about this preparation nobody else's explains.
 *
 * She dissolves several vials into the *same* millilitre, so the syringe never
 * gets fuller while the dose keeps rising. Without the sentence it looks like a
 * mistake she is making, and the instinct — add more solvent — is the one thing
 * that would change her dose.
 */
const CONCENTRATION_RISES: ProtocolWarning = {
  id: "menopur.concentration-rises",
  severity: "caution",
  escalation: "none",
  copy: {
    en: "Important - The total concentration increases with each vial but the volume stays constant, ensuring a comfortable single injection. Use the same diluent volume throughout the entire process.",
    fr: "Important - La concentration totale augmente à chaque flacon mais le volume reste constant, ce qui permet une injection unique et confortable. Utilisez toujours le même volume de diluant pendant toute la procédure.",
  },
  sourceSegment:
    "⚠ Important - The total concentration increases with each vial but the volume stays constant, ensuring a comfortable single injection. Use the same diluent volume throughout the entire process.",
};

const AT_THE_NEEDLE = [WHEN_TO_CALL, BREATHING_OR_ALLERGIC_REACTION];

/**
 * The two things the European walk has to settle before anything is opened.
 *
 * Neither exists in the US, where Menopur is sold as the 75 IU vial alone and
 * subcutaneously alone.
 */
const EEA_OPENING: ProtocolStep[] = [
  {
    // The route this walk does not cover. The UK SmPC permits intramuscular use
    // for the 75 and 150 IU vials and no patient-facing document teaches it, so
    // the walk cannot guide it — but saying nothing would hand a patient
    // prescribed it a subcutaneous procedure with no sign that it is one.
    //
    // The first draft opened with the leaflet's own sentence, "You will either
    // be given this medicine by a doctor or nurse or you will be taught how to
    // give it to yourself." Anaïs read it on screen and could not tell whether
    // she was meant to continue or wait for a nurse. It describes a general
    // situation; she has already opened a self-injection walk, so the half
    // about a nurse giving it to her is noise that only introduces doubt. What
    // is left is one condition and an explicit way past it.
    id: "menopur.eea.route",
    kind: "verify",
    requiresConfirmation: true,
    copy: {
      en: "These steps are for injecting under the skin - If your clinic told you to inject into a muscle instead, stop here and ask them to show you: these steps only cover injections under the skin. Otherwise, carry on.",
      fr: "Ces étapes concernent l'injection sous la peau - Si votre centre vous a dit d'injecter dans un muscle, arrêtez-vous ici et demandez-lui de vous montrer : ces étapes ne couvrent que les injections sous la peau. Sinon, continuez.",
    },
  },
  {
    // Menopur 600 IU and 1200 IU are **multidose**: "After reconstitution, the
    // solution may be stored for a maximum of 28 days at room temperature."
    // Both are powder-and-solvent, so they reach this walk through the same
    // button as the 75 and 150 IU vials — and this walk's aftercare says to
    // throw away what is left, which for those two is most of her cycle.
    //
    // An earlier version of this step told her to "stop here" and then offered
    // "next step", because a step cannot end a walk. It no longer pretends to:
    // it states the difference, and the stop warning beside it is the thing
    // that actually pauses the session. The real answer is a question asked
    // before the walk starts, next to the device confirmation (Anaïs,
    // 2026-09-28) — that is built separately.
    id: "menopur.eea.check-strength",
    kind: "verify",
    requiresConfirmation: true,
    copy: {
      en: "Check the strength on your vial - Look at the number printed on the powder vial. This medicine comes in four strengths: 75, 150, 600 and 1200 IU, and these steps are written for the 75 IU and 150 IU vials, which are mixed fresh for each injection. The 600 IU and 1200 IU vials are mixed once and then kept for up to 28 days. If that is not what your vial says, correct the strength in your medication record.",
      fr: "Vérifiez le dosage sur votre flacon - Regardez le chiffre imprimé sur le flacon de poudre. Ce médicament existe en quatre dosages : 75, 150, 600 et 1200 UI, et ces étapes sont écrites pour les flacons de 75 UI et 150 UI, reconstitués frais à chaque injection. Les flacons de 600 UI et 1200 UI se reconstituent une seule fois, puis se conservent jusqu'à 28 jours. Si ce n'est pas ce qu'indique votre flacon, corrigez le dosage dans votre fiche médicament.",
    },
    warnings: [
      {
        id: "menopur.multidose-vial",
        severity: "stop",
        escalation: "clinic-soon",
        copy: {
          en: "Stop if your vial reads 600 IU or 1200 IU - That is a multidose vial and these steps are not for it. Following them would have you throw away solution you are meant to keep for up to 28 days. Ask your clinic to show you the steps that go with that vial.",
          fr: "Arrêtez-vous si votre flacon indique 600 UI ou 1200 UI - C'est un flacon multidose et ces étapes ne sont pas faites pour lui. Les suivre vous ferait jeter une solution que vous devez garder jusqu'à 28 jours. Demandez à votre centre de vous montrer les étapes qui correspondent à ce flacon.",
        },
        sourceSegment:
          "⚠ Stop if your vial reads 600 IU or 1200 IU - That is a multidose vial and these steps are not for it. Following them would have you throw away solution you are meant to keep for up to 28 days. Ask your clinic to show you the steps that go with that vial.",
      },
    ],
  },
];

/** Everything both markets agree on, which is most of it. */
function sharedSteps(market: "us" | "eea"): ProtocolStep[] {
  return [
    {
      id: `menopur.${market}.prepare`,
      kind: "prepare",
      requiresConfirmation: false,
      copy: {
        en: "Prepare - Wash hands and gather all powder vials, the solvent, a syringe, the reconstitution needle or vial adapter that came with your medicine, and your injection needle. Your clinic will tell you which to use. Check all expiry dates.",
        fr: "Préparer - Lavez-vous les mains et préparez tous les flacons de poudre, le solvant, une seringue, l'aiguille de reconstitution ou l'adaptateur de flacon fourni avec votre médicament, et votre aiguille d'injection. Votre centre vous dira lesquels utiliser. Vérifiez toutes les péremptions.",
      },
      warnings: [MISSED_DOSE],
    },
    {
      // In the UK the 75 and 150 IU solvent is a glass ampoule she snaps, not a
      // vial she pierces — a different action, and a common laceration point.
      // The step names both rather than assuming the American container.
      id: `menopur.${market}.draw-diluent`,
      kind: "prepare",
      requiresConfirmation: false,
      copy: {
        en: "Draw the solvent - Take up all the solvent (typically 1 mL). If it comes in a glass ampoule, break it with the dot pointing towards yourself.",
        fr: "Aspirer le solvant - Prélevez tout le solvant (généralement 1 mL). S'il se présente en ampoule de verre, cassez-la en orientant le point vers vous.",
      },
    },
  ];
}

/** Everything after the vial count, which both markets also agree on. */
function mixAndInject(market: "us" | "eea"): ProtocolStep[] {
  return [
    {
      // "Do NOT shake" is the whole step. Shaking denatures the protein and she
      // has no way to see that it happened. Not "first vial": a 75 IU vial and
      // a 150 IU dose are the ordinary case, but a one-vial dose is real and
      // she should not be counting toward a second that does not exist.
      id: `menopur.${market}.reconstitute`,
      kind: "prepare",
      requiresConfirmation: false,
      copy: {
        en: "Reconstitute - Inject the diluent into the first powder vial, swirl gently until dissolved - do NOT shake.",
        fr: "Reconstituer - Injectez le diluant dans le premier flacon de poudre, faites tourner doucement jusqu'à dissolution - NE PAS agiter.",
      },
    },
    {
      id: `menopur.${market}.reconstitute-additional`,
      kind: "prepare",
      requiresConfirmation: false,
      copy: {
        en: "If your dose needs more than one vial - Using the same syringe, draw the dissolved solution back up and inject it into the second powder vial. Swirl gently until dissolved. Repeat for any additional powder vials - always dissolve sequentially into the same syringe to keep the total volume small (staying at approximately 1 mL).",
        fr: "Si votre dose demande plus d'un flacon - Avec la même seringue, aspirez la solution dissoute et injectez-la dans le second flacon de poudre. Faites tourner doucement jusqu'à dissolution. Répétez pour chaque flacon supplémentaire - dissolvez toujours successivement dans la même seringue pour garder un volume réduit (environ 1 mL).",
      },
      // Conditional in its own first words, because a patient with one vial is
      // otherwise told to inject into "the second powder vial" — an instruction
      // she cannot carry out, at the point where she is holding a syringe of
      // dissolved medicine. Her own form's two powder buttons do not tell one
      // case from the other: they describe what her box looks like, and a 150 IU
      // dose is two 75 IU vials whichever button she pressed.
      warnings: [CONCENTRATION_RISES],
    },
    {
      id: `menopur.${market}.draw-dose`,
      kind: "prepare",
      requiresConfirmation: false,
      copy: {
        en: "Draw the final dose - After the last vial is dissolved, draw the complete solution into the syringe. Tap to remove air bubbles and push them out. Switch to the injection needle.",
        fr: "Prélever la dose finale - Une fois le dernier flacon dissous, aspirez la solution complète dans la seringue. Tapotez pour chasser les bulles, puis évacuez l'air. Changez pour l'aiguille d'injection.",
      },
    },
    {
      id: `menopur.${market}.clean-site`,
      kind: "prepare",
      requiresConfirmation: false,
      copy: {
        en: "Clean the injection site - Swab your lower abdomen, 1-2 inches below the navel, with an alcohol swab and let dry. Alternate between left and right sides each day.",
        fr: "Désinfecter le site - Désinfectez le bas du ventre, à 2-5 cm sous le nombril, à l'alcool et laissez sécher. Alternez les côtés chaque jour.",
      },
    },
    {
      // "Inject immediately after preparation" is the last sentence on purpose:
      // reconstituted menotropins are not stored, in either market.
      id: `menopur.${market}.inject`,
      kind: "irreversible",
      requiresConfirmation: true,
      copy: {
        en: "Inject - Pinch the skin on your lower abdomen, 1-2 inches below the navel, alternating between left and right sides. Hold the syringe at a right angle to your skin and insert the needle in one swift motion. Inject slowly, then hold for 10 seconds before withdrawing. Inject immediately after preparation.",
        fr: "Injecter - Pincez la peau du bas du ventre, à 2-5 cm sous le nombril, en alternant les côtés. Tenez la seringue perpendiculaire à la peau et insérez l'aiguille d'un geste franc. Injectez lentement, puis maintenez 10 secondes avant de retirer. Injectez immédiatement après préparation.",
      },
      warnings: AT_THE_NEEDLE,
    },
    {
      // Everything on the table is single use, including the solution still in
      // the vials — the one preparation here where something is visibly left
      // over and the instinct is to keep it.
      id: `menopur.${market}.aftercare`,
      kind: "aftercare",
      requiresConfirmation: false,
      copy: {
        en: "After injection - Apply gentle pressure with gauze - do not rub. Put your used needles and syringe straight into a sharps container. Everything here is single use: the needles, the syringe and any solution left in the vials, which is thrown away rather than kept for tomorrow.",
        fr: "Après l'injection - Appliquez une légère pression avec une compresse - sans frotter. Jetez aussitôt les aiguilles et la seringue usagées dans un collecteur. Tout est à usage unique ici : les aiguilles, la seringue et la solution restée dans les flacons, qui se jette plutôt que de se garder pour le lendemain.",
      },
    },
  ];
}

/** The one step that is not the same on both sides of the Atlantic. */
const HOW_MANY_US: ProtocolStep = {
  id: "menopur.us.how-many-vials",
  kind: "verify",
  requiresConfirmation: true,
  copy: {
    en: "How many vials - Your clinic will tell you how many vials to use. You can use the liquid in the syringe to mix up to 5 more vials of medicine - six in total.",
    fr: "Combien de flacons - Votre centre vous dira combien de flacons utiliser. Vous pouvez utiliser la solution de la seringue pour dissoudre jusqu'à 5 flacons de plus - six au total.",
  },
};

const HOW_MANY_EEA: ProtocolStep = {
  id: "menopur.eea.how-many-vials",
  kind: "verify",
  requiresConfirmation: true,
  copy: {
    en: "How many vials - Your clinic will tell you how many vials to use. Up to three powder vials can be dissolved with one ampoule of solvent.",
    fr: "Combien de flacons - Votre centre vous dira combien de flacons utiliser. Jusqu'à trois flacons de poudre peuvent être dissous avec une ampoule de solvant.",
  },
};

const SHARED = {
  medicationSlug: "menopur",
  brands: BRANDS,
  aliases: ["Menotropins", "Ménotropines", "hMG", "Menogon"],
  indication: "stimulation",
  // In IU, as the approved content doses it ("75-450 IU daily"), and titrated:
  // her clinic sets the number and changes it mid-course, so there are no
  // strengths to check it against. Only the unit can be wrong on a record.
  dose: { unit: "IU" },
  form: "vial" as const,
  // Both of the buttons her own form writes for a powder kit. Menopur is one
  // vial or six of the same thing, and which she was dispensed is a fact about
  // her prescription, not about the procedure.
  prescriptionForms: ["lyophilized_powder", "multi_vial"],
  route: "subcutaneous" as const,
  version: "2026-09-28.1",
  derivedFrom: "content/data/medications/gonadotropins.json#menopur",
} satisfies Partial<InjectionProtocol>;

export const MENOPUR_US_PROTOCOL: InjectionProtocol = {
  ...SHARED,
  markets: ["US"],
  steps: [...sharedSteps("us"), HOW_MANY_US, ...mixAndInject("us")],
};

export const MENOPUR_EEA_PROTOCOL: InjectionProtocol = {
  ...SHARED,
  // The 75 and 150 IU vials: mixed fresh, every evening.
  presentation: "fresh",
  // What is printed on the glass, which is all she is ever asked to read.
  packageQuantities: [75, 150],
  packageQuantityUnit: "IU",
  markets: [...EEA_MARKETS],
  // Only the European leaflet names the thigh.
  sites: [
    {
      id: "abdomen",
      label: { en: "Lower abdomen", fr: "Bas du ventre" },
      copy: {
        en: "Clean the injection site - Swab your lower abdomen, 1-2 inches below the navel, with an alcohol swab and let dry. Alternate between left and right sides each day.",
        fr: "Désinfecter le site - Désinfectez le bas du ventre, à 2-5 cm sous le nombril, à l'alcool et laissez sécher. Alternez les côtés chaque jour.",
      },
    },
    {
      id: "thigh",
      label: { en: "Front of the thigh", fr: "Face avant de la cuisse" },
      copy: {
        en: "If you use your thigh instead - Swab the front of your thigh with an alcohol swab and let dry. Alternate sides each day.",
        fr: "Si vous utilisez la cuisse - Désinfectez la face avant de la cuisse à l'alcool et laissez sécher. Alternez les côtés chaque jour.",
      },
    },
  ],
  steps: [
    ...EEA_OPENING,
    ...sharedSteps("eea"),
    HOW_MANY_EEA,
    ...mixAndInject("eea"),
  ],
};

export const MENOPUR_PROTOCOLS = [
  MENOPUR_US_PROTOCOL,
  MENOPUR_EEA_PROTOCOL,
] as const;

/**
 * Menopur 600 IU and 1200 IU — the multidose vial, and the first walk here that
 * is shorter the second time.
 *
 * Every other procedure in this catalogue is the same every evening. This one
 * is not: the vial is reconstituted **once** and then kept for up to 28 days,
 * and every later dose is drawn from it with one of the nine administration
 * syringes the pack supplies — graduated in FSH/LH IU, so she reads her dose
 * straight off the barrel rather than converting anything.
 *
 * So there are two walks. The first mixes and injects; the second only draws
 * and injects, and it exists because walking her through dissolving a vial that
 * is already dissolved would have her open a second one for nothing.
 *
 * Which of the two she gets is decided before either starts, from the doses
 * logged against **this calendar line** — `patientMedicationId`, never the
 * medication name, so a second line for the same medicine cannot answer for
 * this one (Anaïs, 2026-09-28). A derived answer is a presumption, so the walk
 * says which one it assumed and lets her say she is opening a new vial.
 *
 * Subcutaneous only, and that is the label rather than an omission: the 600 and
 * 1200 IU SmPCs read "For subcutaneous use only", where the 75 and 150 IU ones
 * permit both routes.
 */

const MULTIDOSE_SHARED = {
  medicationSlug: "menopur",
  brands: BRANDS,
  aliases: ["Menotropins", "Ménotropines", "hMG", "Menogon"],
  indication: "stimulation",
  // In IU, as the approved content doses it ("75-450 IU daily"), and titrated:
  // her clinic sets the number and changes it mid-course, so there are no
  // strengths to check it against. Only the unit can be wrong on a record.
  dose: { unit: "IU" },
  form: "vial" as const,
  prescriptionForms: ["lyophilized_powder", "multi_vial"],
  route: "subcutaneous" as const,
  markets: [...EEA_MARKETS],
  version: "2026-09-28.1",
  derivedFrom: "content/data/medications/gonadotropins.json#menopur",
  sites: [
    {
      id: "abdomen",
      label: { en: "Lower abdomen", fr: "Bas du ventre" },
      copy: {
        en: "Clean the injection site - Swab your lower abdomen with an alcohol swab and let dry. Alternate the site each day.",
        fr: "Désinfecter le site - Désinfectez le bas du ventre à l'alcool et laissez sécher. Changez de site chaque jour.",
      },
    },
    {
      // The 600 IU leaflet names it beside the abdomen, and the approved span
      // was already in the content. Dropping it here meant the site choice
      // appeared or vanished depending on which vial she was dispensed.
      id: "thigh",
      label: { en: "Front of the thigh", fr: "Face avant de la cuisse" },
      copy: {
        en: "If you use your thigh instead - Swab the front of your thigh with an alcohol swab and let dry. Alternate the site each day.",
        fr: "Si vous utilisez la cuisse - Désinfectez la face avant de la cuisse à l'alcool et laissez sécher. Changez de site chaque jour.",
      },
    },
  ],
} satisfies Partial<InjectionProtocol>;

/** Drawing the dose and giving it — identical on the first evening and the last. */
function drawAndInject(day: "first" | "later"): ProtocolStep[] {
  return [
    {
      // The syringes are graduated in IU, which is why no step here converts
      // anything: she reads her prescribed number off the barrel.
      id: `menopur.multidose-${day}.draw-dose`,
      kind: "verify",
      requiresConfirmation: true,
      copy: {
        en: "Draw your dose - Take an administration syringe, whose needle is already fixed, and insert it vertically into the centre of the vial. Turn the vial upside down and draw the dose your clinic prescribed - these syringes are graduated in IU, so you read your dose straight off them.",
        fr: "Prélever la dose - Prenez une seringue d'administration, dont l'aiguille est déjà fixée, et insérez-la verticalement au centre du flacon. Retournez le flacon et prélevez la dose prescrite par votre centre - ces seringues sont graduées en UI, vous lisez donc votre dose directement dessus.",
      },
    },
    {
      id: `menopur.multidose-${day}.clean-site`,
      kind: "prepare",
      requiresConfirmation: false,
      copy: {
        en: "Clean the injection site - Swab your lower abdomen with an alcohol swab and let dry. Alternate the site each day.",
        fr: "Désinfecter le site - Désinfectez le bas du ventre à l'alcool et laissez sécher. Changez de site chaque jour.",
      },
    },
    {
      id: `menopur.multidose-${day}.inject`,
      kind: "irreversible",
      requiresConfirmation: true,
      copy: {
        en: "Inject - Pinch the skin to produce a fold and insert the needle in one swift motion at 90 degrees to the body. Press down on the plunger gently to inject the solution and then remove the needle.",
        fr: "Injecter - Pincez la peau pour former un pli et insérez l'aiguille d'un geste franc, à 90 degrés par rapport au corps. Appuyez doucement sur le piston pour injecter la solution, puis retirez l'aiguille.",
      },
      warnings: [WHEN_TO_CALL, BREATHING_OR_ALLERGIC_REACTION],
    },
    {
      // The one aftercare in this catalogue that says to KEEP the vial. Saying
      // the opposite here — as the fresh-mix walk does, correctly, for the 75
      // and 150 IU vials — would have her bin most of her cycle.
      id: `menopur.multidose-${day}.aftercare`,
      kind: "aftercare",
      requiresConfirmation: false,
      copy: {
        en: "After injection - Put every used syringe and needle straight into a sharps container, including the reconstitution needle. Keep the vial: it holds the rest of your doses. Store it at not more than 25°C and use it within 28 days of mixing. Do not freeze it.",
        fr: "Après l'injection - Jetez aussitôt dans un collecteur toutes les seringues et aiguilles usagées, y compris l'aiguille de reconstitution. Gardez le flacon : il contient le reste de vos doses. Conservez-le à 25 °C maximum et utilisez-le dans les 28 jours suivant le mélange. Ne le congelez pas.",
      },
    },
  ];
}

/**
 * Which vial these steps are written for, said inside the walk.
 *
 * The question before the walk chooses the procedure; this says, where she can
 * still act on it, what the procedure she is in assumes — and where to correct
 * it if her record is wrong. Anaïs, 2026-10-01: a value she typed when adding
 * the medication is used as given, so the walk has to carry the mention rather
 * than ask her to confirm it every evening.
 */
const MULTIDOSE_STRENGTH: ProtocolStep = {
  id: "menopur.multidose.check-strength",
  kind: "verify",
  requiresConfirmation: true,
  copy: {
    en: "Check the strength on your vial - Look at the number printed on the powder vial. This medicine comes in four strengths: 75, 150, 600 and 1200 IU, and these steps are written for the 600 IU and 1200 IU vials, which are mixed once and then kept for up to 28 days. If that is not what your vial says, correct the strength in your medication record.",
    fr: "Vérifiez le dosage sur votre flacon - Regardez le chiffre imprimé sur le flacon de poudre. Ce médicament existe en quatre dosages : 75, 150, 600 et 1200 UI, et ces étapes sont écrites pour les flacons de 600 UI et 1200 UI, qui se reconstituent une seule fois puis se conservent jusqu'à 28 jours. Si ce n'est pas ce qu'indique votre flacon, corrigez le dosage dans votre fiche médicament.",
  },
};

/** The first evening: the vial is powder, and it is dissolved once. */
export const MENOPUR_MULTIDOSE_FIRST_PROTOCOL: InjectionProtocol = {
  ...MULTIDOSE_SHARED,
  presentation: "multidose-first",
  // Both multidose walks answer to the same two container sizes. Which of them
  // she gets is the evening, not the vial — see `presentationDecision`.
  packageQuantities: [600, 1200],
  packageQuantityUnit: "IU",
  steps: [
    MULTIDOSE_STRENGTH,
    {
      id: "menopur.multidose-first.prepare",
      kind: "prepare",
      requiresConfirmation: false,
      preparesTheVial: true,
      copy: {
        en: "Prepare - Wash hands and check the expiry date on the vial. The pack holds the powder vial, a pre-filled syringe of solvent, a reconstitution needle and a set of administration syringes with their needles already fixed.",
        fr: "Préparer - Lavez-vous les mains et vérifiez la date de péremption du flacon. La boîte contient le flacon de poudre, une seringue préremplie de solvant, une aiguille de reconstitution et un jeu de seringues d'administration dont l'aiguille est déjà fixée.",
      },
      warnings: [MISSED_DOSE],
    },
    {
      id: "menopur.multidose-first.attach-needle",
      kind: "prepare",
      requiresConfirmation: false,
      preparesTheVial: true,
      copy: {
        en: "Attach the reconstitution needle - Firmly attach the thick reconstitution needle to the pre-filled syringe of solvent.",
        fr: "Fixer l'aiguille de reconstitution - Fixez fermement l'aiguille épaisse de reconstitution sur la seringue préremplie de solvant.",
      },
    },
    {
      // Slowly, and through the centre of the stopper: the leaflet ties both to
      // avoiding bubbles, which is what the next step cannot fix by shaking.
      id: "menopur.multidose-first.add-solvent",
      kind: "prepare",
      requiresConfirmation: false,
      preparesTheVial: true,
      copy: {
        en: "Add the solvent - Insert the needle vertically through the centre of the rubber stopper of the powder vial and slowly inject all of the solvent.",
        fr: "Ajouter le solvant - Insérez l'aiguille verticalement au centre du bouchon en caoutchouc du flacon de poudre et injectez lentement tout le solvant.",
      },
    },
    {
      // The one manipulation she will physically fight: the plunger pushes back
      // and nothing told her that was expected.
      id: "menopur.multidose-first.plunger",
      kind: "prepare",
      requiresConfirmation: false,
      preparesTheVial: true,
      copy: {
        en: "Let the plunger rise - Adding the solvent creates a slight over-pressure in the vial. Let go of the syringe plunger and let it rise up by itself for about 10 seconds.",
        fr: "Laissez le piston remonter - L'ajout du solvant crée une légère surpression dans le flacon. Lâchez le piston de la seringue et laissez-le remonter tout seul pendant environ 10 secondes.",
      },
    },
    {
      // Not shaking is the whole step, and here it has a visible test beside
      // it: two minutes, and a clear solution.
      id: "menopur.multidose-first.dissolve",
      kind: "verify",
      requiresConfirmation: true,
      preparesTheVial: true,
      copy: {
        en: "Dissolve - Swirl the solution gently. Do not shake, as this will cause air bubbles to form. The powder should dissolve within 2 minutes to a clear solution. Do not use it if it is cloudy or has particles in it.",
        fr: "Dissoudre - Faites tourner doucement la solution. N'agitez pas, cela ferait apparaître des bulles d'air. La poudre doit se dissoudre en 2 minutes et donner une solution limpide. Ne l'utilisez pas si elle est trouble ou contient des particules.",
      },
    },
    ...drawAndInject("first"),
  ],
};

/** Every evening after: nothing is mixed, because it already is. */
export const MENOPUR_MULTIDOSE_LATER_PROTOCOL: InjectionProtocol = {
  ...MULTIDOSE_SHARED,
  presentation: "multidose-later",
  packageQuantities: [600, 1200],
  packageQuantityUnit: "IU",
  steps: [
    MULTIDOSE_STRENGTH,
    {
      // What this walk assumes, said out loud. It was derived from her logged
      // doses, and a derived answer is a presumption — so she is told what was
      // assumed before she acts on it.
      id: "menopur.multidose-later.later-days",
      kind: "verify",
      requiresConfirmation: true,
      copy: {
        en: "Your dose for today - Your vial is already mixed, so there is nothing to dissolve. Take a fresh administration syringe, draw your dose from the vial, and inject as before.",
        fr: "Votre dose du jour - Votre flacon est déjà mélangé, il n'y a rien à dissoudre. Prenez une seringue d'administration neuve, prélevez votre dose dans le flacon et injectez comme la première fois.",
      },
      warnings: [MISSED_DOSE],
    },
    {
      // The date the vial stops being medicine. A walk that skipped the mixing
      // and said nothing about the 28 days would have her inject from a vial
      // that expired quietly.
      id: "menopur.multidose-later.expired",
      kind: "verify",
      requiresConfirmation: true,
      copy: {
        en: "If it has been more than 28 days - Do not use that vial. Start a new one and tell your clinic.",
        fr: "Si plus de 28 jours se sont écoulés - N'utilisez pas ce flacon. Commencez-en un neuf et prévenez votre centre.",
      },
    },
    ...drawAndInject("later"),
  ],
};

export const MENOPUR_MULTIDOSE_PROTOCOLS = [
  MENOPUR_MULTIDOSE_FIRST_PROTOCOL,
  MENOPUR_MULTIDOSE_LATER_PROTOCOL,
] as const;
